// (B) notification 어댑터 — sonner toast(antd 미사용). D4: antd notification → sonner.
// sonner `toast` 는 realm(window)별 모듈 전역 + KitThemeHost <Toaster/>(D4-1a) 가 렌더 →
// main/popup(EmailEditorWindow=별도 JS realm) 자동 격리(per-realm bridge/injected 불필요).
//
// antd notification 파리티: top-right 카드(제목+설명+닫기 X), 기본 4.5초. per-toast position/className
// 으로 message(top-center pill)와 분리(§omToast.css). backendProps 의 antd 옵션(placement/closeIcon/
// className/onClose)을 sonner 로 재매핑한다(showResult 가 placement:'bottom'·closeIcon:false 로 사용).
import { toast } from 'sonner'
import type { OmwNotificationApi, OmwNotificationOptions } from '../notification/useNotification'
type OmNotificationApi = OmwNotificationApi
type OmNotificationOptions = OmwNotificationOptions
import { notificationStack, NOTIFICATION_TOASTER_ID, RESULT_TOASTER_ID } from './toastStack'

type SonnerPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'

// antd placement → sonner position. antd notification 기본은 topRight.
const PLACEMENT_MAP: Record<string, SonnerPosition> = {
  topLeft: 'top-left',
  topRight: 'top-right',
  bottomLeft: 'bottom-left',
  bottomRight: 'bottom-right',
  top: 'top-center',
  bottom: 'bottom-center',
}

interface SonnerNotiOpts {
  toasterId: string
  position: SonnerPosition
  className: string
  closeButton: boolean
  duration: number
  description?: OmNotificationOptions['description']
  id?: string
  onDismiss?: () => void
  onAutoClose?: () => void
}

// antd notification 옵션 → sonner toast 옵션.
// duration: antd 는 '초'(기본 4.5, 0=자동닫힘 없음) → sonner 는 'ms'(Infinity=유지).
// placement→position, closeIcon(false=닫기 숨김)→closeButton, key→id, onClose→onDismiss/onAutoClose.
// className 은 antd 파리티 카드 스타일(.om-toast-notification)에 소비처 className 을 덧붙인다.
function toSonnerOpts(opts: OmNotificationOptions): SonnerNotiOpts {
  const bp = (opts.backendProps ?? {}) as Record<string, unknown>
  const placement = typeof bp.placement === 'string' ? bp.placement : undefined
  const extraClass = typeof bp.className === 'string' ? bp.className : ''
  const onClose = typeof bp.onClose === 'function' ? (bp.onClose as () => void) : undefined

  const durationSec = opts.duration
  const duration = durationSec == null ? 4500 : durationSec <= 0 ? Infinity : durationSec * 1000

  return {
    // toasterId 는 호출부(typed=notification / open=result)에서 지정.
    toasterId: NOTIFICATION_TOASTER_ID,
    position: (placement && PLACEMENT_MAP[placement]) || 'top-right',
    className: `om-toast-notification${extraClass ? ` ${extraClass}` : ''}`,
    // antd notification 은 기본 닫기 X 노출. closeIcon===false 일 때만 숨김(showResult).
    closeButton: bp.closeIcon !== false,
    duration,
    ...(opts.description != null ? { description: opts.description } : null),
    ...(opts.key != null ? { id: opts.key } : null),
    ...(onClose ? { onDismiss: onClose, onAutoClose: onClose } : null),
  }
}

type NotiKind = 'success' | 'error' | 'info' | 'warning'

// 타입 notification(카드): notification Toaster 로 보내고 활성 개수를 notificationStack 에 반영(생성 add,
// 닫힘 remove). KitThemeHost 가 count>3 이면 Toaster expand=false(겹침)로 전환해 antd stack threshold(3)를 재현.
// onDismiss(닫기/스와이프)·onAutoClose(시간초과) 로 제거, 소비처(onClose) 콜백은 체이닝.
function showTracked(kind: NotiKind, opts: OmNotificationOptions): void {
  const sonnerOpts = toSonnerOpts(opts)
  sonnerOpts.toasterId = NOTIFICATION_TOASTER_ID
  let id: string | number | undefined
  const prevDismiss = sonnerOpts.onDismiss
  const prevAuto = sonnerOpts.onAutoClose
  sonnerOpts.onDismiss = () => {
    prevDismiss?.()
    notificationStack.remove(id)
  }
  sonnerOpts.onAutoClose = () => {
    prevAuto?.()
    notificationStack.remove(id)
  }
  id = toast[kind](opts.message, sonnerOpts)
  notificationStack.add(id)
}

// 타입 없는 커스텀 토스트(showResult 바·export 진행상태). 별도 result Toaster 로 보내 notification/message 의
// stack·hover-expand 와 격리(→ 다른 토스트 hover 시 showResult 크기가 변하지 않음). stack 카운트 미포함.
function showResultToast(opts: OmNotificationOptions): void {
  const sonnerOpts = toSonnerOpts(opts)
  sonnerOpts.toasterId = RESULT_TOASTER_ID
  void toast(opts.message, sonnerOpts)
}

const api: OmNotificationApi = {
  success: (opts) => showTracked('success', opts),
  error: (opts) => showTracked('error', opts),
  info: (opts) => showTracked('info', opts),
  warning: (opts) => showTracked('warning', opts),
  // 타입 없는 커스텀 토스트(커스텀 render message — showResult/export 진행상태).
  open: (opts) => showResultToast(opts),
  destroy: (key) => {
    if (key != null) {
      toast.dismiss(key)
      notificationStack.remove(key)
    } else {
      toast.dismiss()
      notificationStack.clear()
    }
  },
}

// React hook / 비React 싱글톤 동일(sonner 전역 — App 컨텍스트·bridge 불필요).
export function useNotificationImpl(): OmNotificationApi {
  return api
}

export const omwNotification: OmwNotificationApi = api
